import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ImageIcon, PlayCircle } from 'lucide-react';
import { FONT_SANS } from '../constants';

const Wrap = styled.div`
  width: 100%;
  max-width: 320px;
`;

const Device = styled.div`
  position: relative;
  border-radius: 36px;
  padding: 12px;
  background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
  box-shadow:
    0 24px 48px rgba(15, 23, 42, 0.18),
    0 0 0 1px rgba(255, 255, 255, 0.06) inset;
`;

const Notch = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: 96px;
  height: 24px;
  background: #0f172a;
  border-radius: 0 0 14px 14px;
  z-index: 2;
`;

const Screen = styled.div`
  position: relative;
  aspect-ratio: 9 / 19.5;
  border-radius: 28px;
  overflow: hidden;
  background: #f8fafc;
`;

const Placeholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  background: linear-gradient(160deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #64748b;
`;

const PlaceholderIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const PlaceholderTitle = styled.p`
  margin: 0;
  font-family: ${FONT_SANS};
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  line-height: 1.4;
`;

const PlaceholderHint = styled.p`
  margin: 0;
  font-family: ${FONT_SANS};
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.45;
`;

const MediaImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CarouselLayer = styled.div`
  position: absolute;
  inset: 0;
`;

const CarouselNav = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 6px;
  pointer-events: none;
  z-index: 3;
`;

const CarouselBtn = styled.button`
  pointer-events: auto;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: background 0.2s;

  &:hover {
    background: rgba(15, 23, 42, 0.78);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const DotRow = styled.div`
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 6px;
  z-index: 3;
`;

const Dot = styled.button`
  width: 6px;
  height: 6px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#fff' : 'rgba(255, 255, 255, 0.45)')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  transition: background 0.2s, transform 0.2s;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const MediaVid = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Caption = styled.p`
  margin: 14px 0 0;
  font-family: ${FONT_SANS};
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
`;

const AUTOPLAY_MS = 4500;

/**
 * iPhone-style media slot. Pass `src` or `srcs` (carousel) when assets are in public/img/.
 */
function MediaFrame({ label, src, srcs, videoSrc, poster, caption }) {
  const slides = srcs?.length ? srcs : src ? [src] : [];
  const [index, setIndex] = useState(0);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const [tabHidden, setTabHidden] = useState(() =>
    typeof document !== 'undefined' ? document.hidden : false,
  );
  const wrapRef = useRef(null);
  const hasMedia = Boolean(videoSrc || slides.length);
  const isCarousel = slides.length > 1;
  const activeIndex = isCarousel ? index % slides.length : 0;
  const activeSrc = slides[activeIndex];

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !isCarousel) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isCarousel]);

  useEffect(() => {
    if (!isCarousel || !inView || hoverPaused || tabHidden) return undefined;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return undefined;

    const timer = window.setInterval(goNext, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [isCarousel, inView, hoverPaused, tabHidden, goNext]);

  useEffect(() => {
    const onVisibilityChange = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  return (
    <Wrap
      ref={wrapRef}
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
    >
      <Device aria-label={label}>
        <Notch aria-hidden="true" />
        <Screen>
          {videoSrc ? (
            <MediaVid src={videoSrc} poster={poster} muted loop playsInline controls={false} autoPlay />
          ) : activeSrc ? (
            <>
              {isCarousel ? (
                <AnimatePresence mode="wait" initial={false}>
                  <CarouselLayer
                    key={activeSrc}
                    as={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <MediaImg src={activeSrc} alt={`${label} (${activeIndex + 1}/${slides.length})`} loading="lazy" />
                  </CarouselLayer>
                </AnimatePresence>
              ) : (
                <MediaImg src={activeSrc} alt={label} loading="lazy" />
              )}
              {isCarousel && (
                <>
                  <CarouselNav aria-hidden={false}>
                    <CarouselBtn type="button" aria-label="Previous image" onClick={goPrev}>
                      <ChevronLeft size={16} />
                    </CarouselBtn>
                    <CarouselBtn type="button" aria-label="Next image" onClick={goNext}>
                      <ChevronRight size={16} />
                    </CarouselBtn>
                  </CarouselNav>
                  <DotRow role="tablist" aria-label={`${label} slides`}>
                    {slides.map((slideSrc, i) => (
                      <Dot
                        key={slideSrc}
                        type="button"
                        role="tab"
                        aria-selected={i === activeIndex}
                        aria-label={`Slide ${i + 1} of ${slides.length}`}
                        $active={i === activeIndex}
                        onClick={() => setIndex(i)}
                      />
                    ))}
                  </DotRow>
                </>
              )}
            </>
          ) : (
            <Placeholder>
              <PlaceholderIcon aria-hidden="true">
                {videoSrc !== undefined ? <PlayCircle size={24} /> : <ImageIcon size={24} />}
              </PlaceholderIcon>
              <PlaceholderTitle>{label}</PlaceholderTitle>
              <PlaceholderHint>
                Drop a simulator screenshot or screen recording into{' '}
                <code style={{ fontSize: 10 }}>public/media/</code>
              </PlaceholderHint>
            </Placeholder>
          )}
        </Screen>
      </Device>
      {caption && !hasMedia ? <Caption>{caption}</Caption> : null}
    </Wrap>
  );
}

export default MediaFrame;
